// Ventes Flash - Espace Vendeur
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, Plus, Clock, Calendar, Percent, Package, Eye, Edit, Trash2,
  Play, Pause, CheckCircle2, XCircle, TrendingUp, Users, ShoppingCart,
  Timer, AlertCircle, ChevronRight, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Statuts des ventes flash
const FLASH_STATUSES = {
  scheduled: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Programmée', icon: Clock },
  active: { color: 'bg-green-100 text-green-700 border-green-200', label: 'En cours', icon: Zap },
  ended: { color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Terminée', icon: CheckCircle2 },
  cancelled: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Annulée', icon: XCircle },
};

// Countdown Timer Component
function Countdown({ endDate }) {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const diff = end - now;

      if (diff > 0) {
        setTimeLeft({
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  return (
    <div className="flex items-center gap-1 font-mono">
      <span className="bg-gray-900 text-white px-2 py-1 rounded text-sm font-bold">
        {String(timeLeft.hours).padStart(2, '0')}
      </span>
      <span className="text-gray-500">:</span>
      <span className="bg-gray-900 text-white px-2 py-1 rounded text-sm font-bold">
        {String(timeLeft.minutes).padStart(2, '0')}
      </span>
      <span className="text-gray-500">:</span>
      <span className="bg-gray-900 text-white px-2 py-1 rounded text-sm font-bold">
        {String(timeLeft.seconds).padStart(2, '0')}
      </span>
    </div>
  );
}

// Carte vente flash
function FlashSaleCard({ sale, onEdit, onToggle, onDelete }) {
  const status = FLASH_STATUSES[sale.status] || FLASH_STATUSES.scheduled;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
    >
      {/* Header avec statut */}
      <div className={`px-4 py-2 flex items-center justify-between ${
        sale.status === 'active' ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gray-100'
      }`}>
        <span className={`flex items-center gap-1.5 text-sm font-medium ${
          sale.status === 'active' ? 'text-white' : 'text-gray-700'
        }`}>
          <StatusIcon size={14} />
          {status.label}
        </span>
        {sale.status === 'active' && <Countdown endDate={sale.endDate} />}
      </div>

      {/* Contenu */}
      <div className="p-4">
        {/* Produit */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {sale.product.image ? (
              <img src={sale.product.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <Package size={24} className="text-gray-300" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 line-clamp-2">{sale.product.title}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-bold text-red-600">{sale.flashPrice.toFixed(2)} €</span>
              <span className="text-sm text-gray-400 line-through">{sale.product.originalPrice.toFixed(2)} €</span>
              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                -{sale.discount}%
              </span>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500 text-xs mb-1">Début</p>
              <p className="font-medium text-gray-900">{sale.startDate}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Fin</p>
              <p className="font-medium text-gray-900">{sale.endDate}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <p className="text-lg font-bold text-blue-700">{sale.stats.views}</p>
            <p className="text-xs text-blue-600">Vues</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-lg font-bold text-green-700">{sale.stats.sales}</p>
            <p className="text-xs text-green-600">Ventes</p>
          </div>
          <div className="text-center p-2 bg-orange-50 rounded-lg">
            <p className="text-lg font-bold text-orange-700">{sale.stock}</p>
            <p className="text-xs text-orange-600">Stock</p>
          </div>
        </div>

        {/* Stock Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-500">Stock restant</span>
            <span className="font-medium text-gray-700">{sale.stock}/{sale.initialStock}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all"
              style={{ width: `${(sale.stock / sale.initialStock) * 100}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {sale.status === 'scheduled' && (
            <Button
              size="sm"
              onClick={() => onToggle(sale, 'active')}
              className="flex-1 bg-green-500 hover:bg-green-600 rounded-lg"
            >
              <Play size={14} className="mr-1" /> Lancer
            </Button>
          )}
          {sale.status === 'active' && (
            <Button
              size="sm"
              onClick={() => onToggle(sale, 'ended')}
              className="flex-1 bg-gray-500 hover:bg-gray-600 rounded-lg"
            >
              <Pause size={14} className="mr-1" /> Terminer
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(sale)}
            className="rounded-lg"
          >
            <Edit size={14} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(sale)}
            className="rounded-lg text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Modal création vente flash
function CreateFlashSaleModal({ isOpen, onClose, onCreate, products }) {
  const [form, setForm] = useState({
    productId: '',
    discount: 20,
    stock: 10,
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '18:00'
  });

  const selectedProduct = products.find(p => p.id === form.productId);
  const flashPrice = selectedProduct ? selectedProduct.price * (1 - form.discount / 100) : 0;

  const handleCreate = () => {
    if (!form.productId || !form.startDate || !form.endDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    onCreate({
      ...form,
      product: selectedProduct,
      flashPrice,
      startDate: `${form.startDate} ${form.startTime}`,
      endDate: `${form.endDate} ${form.endTime}`
    });
    setForm({
      productId: '',
      discount: 20,
      stock: 10,
      startDate: '',
      startTime: '09:00',
      endDate: '',
      endTime: '18:00'
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-orange-500 to-red-500">
          <div className="flex items-center gap-3 text-white">
            <Zap size={24} />
            <div>
              <h2 className="text-xl font-bold">Nouvelle Vente Flash</h2>
              <p className="text-white/80 text-sm">Crée une offre limitée dans le temps</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Sélection produit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produit *
            </label>
            <select
              value={form.productId}
              onChange={(e) => setForm({ ...form, productId: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl"
            >
              <option value="">Sélectionner un produit</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.title} - {product.price.toFixed(2)} €
                </option>
              ))}
            </select>
          </div>

          {/* Réduction et prix */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Réduction (%)
              </label>
              <div className="relative">
                <Input
                  type="number"
                  min={5}
                  max={90}
                  value={form.discount}
                  onChange={(e) => setForm({ ...form, discount: parseInt(e.target.value) || 0 })}
                  className="rounded-xl pr-8"
                />
                <Percent size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stock dédié
              </label>
              <Input
                type="number"
                min={1}
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
                className="rounded-xl"
              />
            </div>
          </div>

          {/* Aperçu prix */}
          {selectedProduct && (
            <div className="bg-orange-50 rounded-xl p-4">
              <p className="text-sm text-orange-700 mb-2">Aperçu du prix flash</p>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-red-600">{flashPrice.toFixed(2)} €</span>
                <span className="text-gray-400 line-through">{selectedProduct.price.toFixed(2)} €</span>
                <span className="px-2 py-1 bg-red-500 text-white rounded-full text-sm font-bold">
                  -{form.discount}%
                </span>
              </div>
            </div>
          )}

          {/* Dates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Période de la vente flash *
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Date de début</p>
                <Input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Heure de début</p>
                <Input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Date de fin</p>
                <Input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Heure de fin</p>
                <Input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
            <AlertCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Conseils pour une vente flash réussie</p>
              <ul className="mt-1 text-blue-600 text-xs space-y-1">
                <li>• Proposez une réduction d'au moins 20%</li>
                <li>• Limitez la durée à 24-48h maximum</li>
                <li>• Choisissez des créneaux de forte affluence</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
            Annuler
          </Button>
          <Button onClick={handleCreate} className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90 rounded-xl">
            <Zap size={16} className="mr-2" />
            Créer la vente flash
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function VentesFlashPage() {
  const [flashSales, setFlashSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Produits simulés
      setProducts([
        { id: 'p1', title: 'Perceuse Bosch Professional GSB 18V', price: 149, image: null },
        { id: 'p2', title: 'Niveau laser Makita SK105DZ', price: 289, image: null },
        { id: 'p3', title: 'Coffret tournevis Facom', price: 89, image: null },
        { id: 'p4', title: 'Meuleuse angulaire 125mm', price: 79, image: null },
        { id: 'p5', title: 'Marteau perforateur SDS+', price: 199, image: null },
      ]);

      // Ventes flash simulées
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      setFlashSales([
        {
          id: 'flash-1',
          product: { id: 'p1', title: 'Perceuse Bosch Professional GSB 18V', originalPrice: 149, image: null },
          flashPrice: 99,
          discount: 34,
          stock: 8,
          initialStock: 15,
          startDate: now.toISOString().split('T')[0] + ' 09:00',
          endDate: new Date(now.getTime() + 6 * 60 * 60 * 1000).toISOString(),
          status: 'active',
          stats: { views: 234, sales: 7 }
        },
        {
          id: 'flash-2',
          product: { id: 'p2', title: 'Niveau laser Makita SK105DZ', originalPrice: 289, image: null },
          flashPrice: 199,
          discount: 31,
          stock: 5,
          initialStock: 5,
          startDate: tomorrow.toISOString().split('T')[0] + ' 10:00',
          endDate: new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] + ' 22:00',
          status: 'scheduled',
          stats: { views: 0, sales: 0 }
        },
        {
          id: 'flash-3',
          product: { id: 'p3', title: 'Coffret tournevis Facom', originalPrice: 89, image: null },
          flashPrice: 59,
          discount: 34,
          stock: 0,
          initialStock: 20,
          startDate: '2025-01-10 09:00',
          endDate: '2025-01-11 18:00',
          status: 'ended',
          stats: { views: 456, sales: 20 }
        },
      ]);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (data) => {
    const newSale = {
      id: `flash-${flashSales.length + 1}`,
      product: { ...data.product, originalPrice: data.product.price },
      flashPrice: data.flashPrice,
      discount: data.discount,
      stock: data.stock,
      initialStock: data.stock,
      startDate: data.startDate,
      endDate: data.endDate,
      status: 'scheduled',
      stats: { views: 0, sales: 0 }
    };
    setFlashSales([newSale, ...flashSales]);
    toast.success('Vente flash créée avec succès !');
  };

  const handleToggle = (sale, newStatus) => {
    setFlashSales(flashSales.map(s => 
      s.id === sale.id ? { ...s, status: newStatus } : s
    ));
    toast.success(newStatus === 'active' ? 'Vente flash lancée !' : 'Vente flash terminée');
  };

  const handleDelete = (sale) => {
    if (window.confirm('Supprimer cette vente flash ?')) {
      setFlashSales(flashSales.filter(s => s.id !== sale.id));
      toast.success('Vente flash supprimée');
    }
  };

  const filteredSales = flashSales.filter(s => filter === 'all' || s.status === filter);

  // Stats globales
  const stats = {
    active: flashSales.filter(s => s.status === 'active').length,
    scheduled: flashSales.filter(s => s.status === 'scheduled').length,
    totalSales: flashSales.reduce((sum, s) => sum + s.stats.sales, 0),
    totalRevenue: flashSales.reduce((sum, s) => sum + (s.stats.sales * s.flashPrice), 0)
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="ventes-flash-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <Zap className="text-orange-500" />
            Ventes Flash
          </h1>
          <p className="text-gray-500">Crée des offres limitées pour booster tes ventes</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90 rounded-xl"
        >
          <Plus size={18} className="mr-2" />
          Nouvelle vente flash
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
          <Zap size={24} className="mb-2 opacity-80" />
          <p className="text-2xl font-bold">{stats.active}</p>
          <p className="text-white/80 text-sm">En cours</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <Clock size={24} className="mb-2 text-blue-500" />
          <p className="text-2xl font-bold text-gray-900">{stats.scheduled}</p>
          <p className="text-gray-500 text-sm">Programmées</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <ShoppingCart size={24} className="mb-2 text-green-500" />
          <p className="text-2xl font-bold text-gray-900">{stats.totalSales}</p>
          <p className="text-gray-500 text-sm">Ventes totales</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <TrendingUp size={24} className="mb-2 text-purple-500" />
          <p className="text-2xl font-bold text-gray-900">{stats.totalRevenue.toFixed(0)} €</p>
          <p className="text-gray-500 text-sm">Revenus flash</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap gap-2">
        {[
          { value: 'all', label: 'Toutes' },
          { value: 'active', label: 'En cours' },
          { value: 'scheduled', label: 'Programmées' },
          { value: 'ended', label: 'Terminées' },
        ].map(item => (
          <button
            key={item.value}
            onClick={() => setFilter(item.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === item.value
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Flash Sales Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredSales.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Zap size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Aucune vente flash</h3>
          <p className="text-gray-500 mb-6">Crée ta première vente flash pour attirer plus de clients</p>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
          >
            <Plus size={18} className="mr-2" />
            Créer une vente flash
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSales.map((sale) => (
            <FlashSaleCard
              key={sale.id}
              sale={sale}
              onEdit={(s) => toast.info('Édition à implémenter')}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateFlashSaleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        products={products}
      />
    </div>
  );
}
